import { FC, Fragment } from 'react'
import {
  SectionArticlesTitle,
} from './styles'
import { LiabilitySectionData } from 'src/dtos/applicationData'
import { handleYesNoAnswers } from 'src/utils/formDataManipulation'
import { numberToCurrency } from 'src/utils/numberFormat'
import { IoMdCheckmark } from 'react-icons/io'
import { TbFileCheck } from 'react-icons/tb'

interface InsuranceSectionProps {
  insuranceSection?: LiabilitySectionData;
  isComplete: boolean;
}

const InsuranceSection: FC<InsuranceSectionProps> = ({ insuranceSection, isComplete }) => {
  const hasClaims = insuranceSection?.pastLiability?.hasMalpracticeClaim === true ||
    insuranceSection?.pastLiability?.hasMalpracticeBasis === true

  return (
    <section>
      <header>
        <h4>Insurance</h4>
        {isComplete ? (
          <h6><IoMdCheckmark/> Complete</h6>
        ) : null}
      </header>
      <SectionArticlesTitle>INSURANCE HISTORY</SectionArticlesTitle>
      <article>
        <strong>Has firm been insured in the past 5 years?</strong>
        <span>{handleYesNoAnswers(insuranceSection?.isFirmRecentlyInsured)}</span>
      </article>
      {insuranceSection?.isFirmRecentlyInsured === true ? (
        <>
          {insuranceSection?.insurances?.map((insurance, index) => (
            <article key={index}>
              <strong>{index === 0 ? 'Most Recent Insurance' : 'Previous Insurance'}</strong>
              <span>
                <h6  style={{ marginTop: 0 }}>Insurance Company: </h6>
                {insurance?.companyName ?? 'N/A'}
              </span>
              <span>
                <h6>Limits of Liability: </h6>
                {insurance?.liabilityLimits ?? 'N/A'}
              </span>
              <span>
                <h6>Deductible: </h6>
                {insurance?.deductible ? numberToCurrency(insurance?.deductible, 0, 0) : 'N/A'}
              </span>
              <span>
                <h6>Expiration Date: </h6>
                {!!insurance?.expirationDate ? insurance?.expirationDate : 'N/A'}
              </span>
              <span>
                <h6>Annual Premium: </h6>
                {insurance?.annualPremium ? numberToCurrency(insurance?.annualPremium, 0, 0) : 'N/A'}
              </span>
              <span>
                <h6>Did you purchase tail coverage for this policy? </h6>
                {handleYesNoAnswers(insurance?.isTailCoverage)}
              </span>
              <span>
                <h6>Dates of Tail Coverage:</h6>
                {(!!insurance?.tailCoverageFromDate && !!insurance?.tailCoverageToDate) ? (
                  <>{insurance?.tailCoverageFromDate} to {insurance?.tailCoverageToDate}</>
                ) : (
                  <>N/A</>
                )}
              </span>
              {index === 0 ? (
                <>
                  <span>
                    <h6>Is this your current insurance? </h6>
                    {handleYesNoAnswers(insurance?.isCurrent)}
                  </span>
                  <span>
                    <h6>Retroactive Date for Prior Acts Coverage:</h6>
                    {!!insurance?.retroactiveDate ? insurance?.retroactiveDate :  "N/A"}
                  </span>
                  <span>
                    <h6>Upload insurance policy info. Please include declarations and endorsements: </h6>
                  </span>
                  {insurance?.policyFile?.map(file => (
                    <span key={file?.id} className="icon">
                      <TbFileCheck/>
                      {file?.fileName ?? 'N/A'}
                    </span>
                  ))}
                </>
              ) : null}
            </article>
          ))}
        </>
      ) : null}
      <SectionArticlesTitle>PAST LIABILITY</SectionArticlesTitle>
      <article>
        <strong>In the past 10 years, has your firm or any lawyer at your firm had liability insurance cancelled, declined, or not renewed?</strong>
        <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasCanceledLiabilities)}</span>
        {insuranceSection?.pastLiability?.hasCanceledLiabilities === true ? 
          insuranceSection?.pastLiability?.canceledLiabilitiesFile?.map(file => (
            <span key={file?.id} className="icon"><TbFileCheck/>{file?.fileName ?? 'N/A'}</span>
          )) : null}
      </article>
      <article>
        <strong>In the past 10 years, has your firm or any lawyer at your firm been the subject of a legal malpractice claim?</strong>
        <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasMalpracticeClaim)}</span>
      </article>
      <article>
        <strong>
          Does any firm member or non-attorney employee of your firm (including predecessor firms) know of any circumstance, 
          act, error, or omission that might form the basis of a legal malpractice claim against your firm? Or any attorney in your firm?
        </strong>
        <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasMalpracticeBasis)}</span>
      </article>
      <article>
        <strong>Has your firm or any lawyer at your firm been the subject of a grievance complain in the past five years?</strong>
        <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasGrievanceComplain)}</span>
        {insuranceSection?.pastLiability?.hasGrievanceComplain === true ? 
          insuranceSection?.pastLiability?.grievanceComplainFile?.map(file => (
            <span key={file?.id} className="icon"><TbFileCheck/>{file?.fileName ?? 'N/A'}</span>
          )) : null}
      </article>
      <article>
        <strong>
          Has your firm or any lawyer at your firm ever voluntarily accepted discipline or been refused admission to the bar, 
          been reprimanded (privately or publicly), suspended from the practice of law, disbarred, received a probationary license, 
          or otherwise disciplined by any disciplinary or licensing authority?
        </strong>
        <span>{handleYesNoAnswers(insuranceSection?.pastLiability?.hasDisciplinedAction)}</span>
        {insuranceSection?.pastLiability?.hasDisciplinedAction === true ? (
          <span>{insuranceSection?.pastLiability?.disciplinedActionDetails ?? 'N/A'}</span>
        ) : null}
      </article>
      {(hasClaims && insuranceSection.pastLiability?.claims && insuranceSection.pastLiability?.claims.length > 0) ? 
        insuranceSection.pastLiability?.claims?.map((claim, index) => (
        <Fragment key={index + 1}>
          <SectionArticlesTitle>CLAIM {index + 1}</SectionArticlesTitle>
          <article>
            <strong>Firm Name</strong>
            <span>{claim?.firmName ?? 'N/A'}</span>
          </article>
          <article>
            <strong>Individuals of firm involved in the claim</strong>
            {claim?.hasInvolvedMembers === false ? (
              <span>I'm not sure</span>
            ) : claim?.involvedMembers ? claim.involvedMembers.map(member => (
              <span key={member}>{member}</span>
            )) : <span>N/A</span>}
          </article>
          <article>
            <strong>Claimant’s Full Name</strong>
            <span>{claim?.hasClaimant === false ? "I'm not sure" : (claim?.claimantFullName ?? 'N/A')}</span>
          </article>
          <article>
            <strong>Claim Type</strong>
            <span>{claim?.claimType ?? 'N/A'}</span>
          </article>
          <article>
            <strong>Date of Claim</strong>
            <span>{claim?.hasClaimDate === false ? "I'm not sure" : (claim?.claimDate ?? 'N/A')}</span>
          </article>
          <article>
            <strong>Date of Alleged Error</strong>
            <span>{claim?.hasAllegedErrorDate === false ? "I'm not sure" : (claim?.allegedErrorDate ?? 'N/A')}</span>
          </article>
          <article>
            <strong>Is this claim closed?</strong>
            <span>{claim?.isClaimClosed ?? 'N/A'}</span>
            {claim?.isClaimClosed === 'Yes' ? (
              <>
                <span>
                  <h6>Indicate how the claim was closed: </h6>
                  {claim?.closingType ?? 'N/A'}
                </span>
                <span>
                  <h6>Total loss paid including deductibles: </h6>
                  {claim?.totalLossPaid ? numberToCurrency(claim?.totalLossPaid) : 'N/A'}
                </span>
                <span>
                  <h6>Defense cost paid: </h6>
                  {claim?.defenseCostPaid ? numberToCurrency(claim?.defenseCostPaid) : 'N/A'}
                </span>
              </>
            ) : claim?.isClaimClosed === 'No' ?? null}
          </article>
          <article>
            <strong>Name of Insurer</strong>
            <span>{claim?.hasInsurer === false ? "I'm not sure" : (claim?.insurerName ?? 'N/A')}</span>
          </article>
          <article>
            <strong>Description of Claim</strong>
            <span>
              <h6>Alleged act, error, or omission upon which claimant bases claim:</h6>
            </span>
            <span>{claim?.hasClaimDescription === false ? "I'm not sure" : (claim?.claimDescription ?? 'N/A')}</span>
            <span>
              <h6>Description of case and events, including area of practice out of which the claim arose:</h6>
            </span>
            <span>{claim?.hasCaseDescription === false ? "I'm not sure" : (claim?.caseDescription ?? 'N/A')}</span>
            <span>
              <h6>Description of the type and extent of injury or damage allegedly sustained:</h6>
            </span>
            <span>{claim?.hasDamageDescription === false ? "I'm not sure" : (claim?.damageDescription ?? 'N/A')}</span>
            <span>
              <h6>Upload any applicable claim information:</h6>
            </span>
            {claim?.claimFile?.map(file => (
              <span key={file?.id} className="icon"><TbFileCheck/>{file?.fileName ?? 'N/A'}</span>
            ))}
          </article>
        </Fragment>
      )) : null}
      <SectionArticlesTitle>REVIEW</SectionArticlesTitle>
      <article>
        <strong>Notes</strong>
        <span>{insuranceSection?.review?.note ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Files</strong>
        {!!insuranceSection?.review?.files && insuranceSection?.review?.files.length > 0 ? 
          insuranceSection?.review?.files.map(file => (
            <span key={file?.id} className="icon"><TbFileCheck /> {file?.fileName ?? "N/A"}</span>
          )) : (
            <span>N/A</span>
          )}
      </article>
    </section>
  )
}

export default InsuranceSection